import { I18n } from "@assets/strings";
import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { allCategories, Category, subCategoriesByCategory } from "@domain/lms/category/category";
import { fromLeft, fromRight } from "@ui/styles/keyframes";
import { gap } from "@ui/styles/utils";
import { ClosableBottomSheet } from "@ui/components/closableBottomSheet";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { ArrowIcon, CategoryIcon } from "@ui/components/icons";
import { CategoryEditorProps } from "./categoryEditorProps";

export const MobileCategoryEditor: React.FC<CategoryEditorProps> = ({
	category,
	subCategory,
	setCategory,
	setSubCategory,
	label,
	...htmlProps
}) => {
	const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
	const [bottomSheetOpened, setBottomSheetOpened] = useState(false);
	const hasSubs = (cat: Category): boolean => subCategoriesByCategory[cat].length > 0;
	const scrollRef = useRef<HTMLDivElement>(null);
	const [animationEnded, setAnimationEnded] = useState(false);

	const close = useCallback(() => {
		setSelectedCategory(null);
		scrollRef.current?.scrollTo(0, 0);
		setBottomSheetOpened(false);
	}, []);

	return (
		<>
			<Container {...htmlProps} onClick={() => setBottomSheetOpened(true)}>
				<CategoryIcon />
				<CategoryPicker>
					{category ? I18n.t(subCategory ? `category.${subCategory}` : `category.${category}`) : label + "*"}
				</CategoryPicker>
			</Container>
			{bottomSheetOpened && (
				<ClosableBottomSheet isOpen={bottomSheetOpened} onClose={close}>
					{!selectedCategory || !hasSubs(selectedCategory) || !setSubCategory ? (
						<Categories
							firstView
							fullHeight
							ref={scrollRef}
							onAnimationEnd={() => setAnimationEnded(true)}
							onAnimationStart={() => setAnimationEnded(false)}
							scrollActived={animationEnded}
						>
							{allCategories.map((cat) => (
								<CategoryRow
									selected={selectedCategory === cat}
									key={cat}
									onClick={() => {
										if (hasSubs(cat) && !!setSubCategory) {
											setSelectedCategory(cat);
										} else {
											setCategory(cat);
											setSubCategory?.();
											close();
										}
									}}
								>
									{I18n.t(`category.${cat}`)}
								</CategoryRow>
							))}
						</Categories>
					) : (
						<>
							<Header>
								<BackButton onClick={() => setSelectedCategory(null)}>
									<ArrowIcon />
								</BackButton>
								{I18n.t(`category.${selectedCategory}`)}
							</Header>
							<Categories
								onAnimationEnd={() => setAnimationEnded(true)}
								onAnimationStart={() => setAnimationEnded(false)}
								scrollActived={animationEnded}
							>
								{subCategoriesByCategory[selectedCategory].map((subCat) => (
									<CategoryRow
										key={subCat}
										onClick={() => {
											setCategory(selectedCategory);
											setSubCategory(subCat);
											close();
										}}
									>
										{I18n.t(`category.${subCat}`)}
									</CategoryRow>
								))}
							</Categories>
						</>
					)}
				</ClosableBottomSheet>
			)}
		</>
	);
};

const Container = styled(RoundedBlock)`
	padding: 10px 16px;
	display: flex;
	align-items: center;
	cursor: pointer;

	svg {
		width: 20px;
		height: 20px;
		margin-right: 10px;
	}
`;

const CategoryPicker = styled.div`
	${({ theme }) => theme.textStyles.primary};
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const Categories = styled.div.withConfig({
	shouldForwardProp: (prop) => !["fullHeight", "firstView", "scrollActived"].includes(prop),
})<{ fullHeight?: boolean; firstView?: boolean; scrollActived?: boolean }>`
	height: ${({ fullHeight }) => (fullHeight ? "100%" : "70%")};
	overflow-y: ${({ scrollActived }) => (scrollActived ? "scroll" : "hidden")};
	${gap(20)};
	padding-right: 46px;
	padding-bottom: 50px;
	animation: ${({ firstView }) => (firstView ? fromLeft : fromRight)} 0.2s linear;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	font-size: 1.25rem;
	font-weight: bold;
	color: ${({ theme }) => theme.colors.primary};
	padding-right: 46px;
	padding-bottom: 37px;
	animation: ${fromRight} 0.2s linear;
`;

const BackButton = styled.div`
	svg {
		margin-right: 24px;
	}
`;

const CategoryRow = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
	${({ theme }) => theme.textStyles.primary};
	${({ theme, selected }) => selected && `color: ${theme.colors.primary}`};
	display: flex;
	transition: color 0.1s linear;
`;
